import { BaseText, Meta, theme } from 'aimo-ui';
import { useParkingZoneSearch } from 'hooks/useParkingZoneSearch';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { useMapStore } from 'stores/mapStore';
import { useSearchStore } from 'stores/searchStore';
import Icon from '../icons/Icon';
import NoMatches from './NoMatches';
import ChargerSearchResultsList from './search/ChargerSearchResultsList';
import ParkzoneResultsList from './search/ParkzoneResultsList';
import RecentSearchesList from './search/RecentSearchesList';
import { Center, Container, IconWrapper, Row } from './styles/styles';

type ParkingZoneSearchProps = {
  searchTerm: string;
  testID?: string;
  withLTP?: boolean;
};

const Hint = () => {
  const { t } = useTranslation();
  return (
    <Meta color={theme.colors.gray300} center>
      {t('search.hint')}
    </Meta>
  );
};

const LocationItem = ({ testID }: { testID: string }) => {
  const { t } = useTranslation();
  const usersLocation = useMapStore((state) => state.userLocation);
  const { setSearchInputActive } = useSearchStore((state) => state);
  return (
    <Pressable onPress={() => setSearchInputActive(false)}>
      <Row testID={testID} mt={0} mb={0}>
        <IconWrapper>
          <Icon
            name="Location"
            size="s"
            color={usersLocation ? undefined : theme.colors.boost.red}
          />
        </IconWrapper>
        <BaseText>{t('search.myLocation')}</BaseText>
      </Row>
    </Pressable>
  );
};

const ParkingZoneSearchList = ({
  searchTerm,
  testID,
  withLTP,
}: ParkingZoneSearchProps) => {
  const { t } = useTranslation();
  const { setSearchInputActive } = useSearchStore((state) => state);
  const currentLocation = useMapStore((state) => state.userLocation);

  const {
    loading,
    error: hasError,
    noResults,
  } = useParkingZoneSearch({
    searchTerm,
    withLTP,
  });

  if (hasError && noResults) {
    return (
      <Container testID="error-text">
        <Center>
          <Meta>{t('parkingZone.errorInLoading')}</Meta>
        </Center>
      </Container>
    );
  }

  if (
    (searchTerm.length > 3 ||
      (!Number.isNaN(Number(searchTerm)) && searchTerm.length >= 2)) &&
    !loading &&
    !hasError &&
    noResults
  ) {
    return <NoMatches onButtonClick={() => setSearchInputActive(false)} />;
  }

  if (searchTerm.length < 2 && searchTerm.length > 0) {
    return <Hint />;
  }

  return (
    <Container testID={testID}>
      {searchTerm.length === 0 && (
        <>
          <LocationItem testID="nearest-locations" />
          {!withLTP && (
            <RecentSearchesList
              currentLocation={currentLocation}
              withLTP={withLTP}
            />
          )}
        </>
      )}
      {searchTerm.length > 0 && (
        <>
          <ParkzoneResultsList
            searchTerm={searchTerm}
            currentLocation={currentLocation}
            withLTP={withLTP}
          />
          {!withLTP && (
            <ChargerSearchResultsList
              searchTerm={searchTerm}
              withLTP={withLTP}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default ParkingZoneSearchList;
